'use client';

import { useState, useMemo } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import ScoreGauge from '@/components/tools/ScoreGauge';
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Sparkles,
  Lightbulb,
  BookOpen,
  CheckCircle2,
  Target,
  Zap,
  BarChart3,
  Users,
  Shield,
  Clock,
  Trophy,
  AlertTriangle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type QuestionType =
  | 'leadership'
  | 'teamwork'
  | 'problem-solving'
  | 'conflict'
  | 'time-management'
  | 'achievement'
  | 'failure';

type StepKey = 'situation' | 'task' | 'action' | 'result';

interface STARInputs {
  situation: string;
  task: string;
  action: string;
  result: string;
}

interface BuiltAnswer {
  polished: string;
  sections: { key: StepKey; label: string; text: string }[];
  score: number;
  feedback: { label: string; score: number; tip: string }[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STEPS: { key: StepKey; label: string; letter: string; color: string; bgColor: string; borderColor: string; prompt: string; helpQuestions: string[] }[] = [
  {
    key: 'situation',
    label: 'Situation',
    letter: 'S',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-400',
    prompt: 'Describe the context or background. Set the scene for your interviewer.',
    helpQuestions: [
      'Where were you working at the time?',
      'What was the project or initiative?',
      'What was the business context or challenge the team faced?',
      'When did this happen (rough timeframe)?',
    ],
  },
  {
    key: 'task',
    label: 'Task',
    letter: 'T',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-400',
    prompt: 'What was your specific role or responsibility? What were you asked or expected to do?',
    helpQuestions: [
      'What specifically was YOUR responsibility (not the team\'s)?',
      'What goal or objective were you working toward?',
      'What constraints or requirements did you face?',
      'Why was this important to the organization?',
    ],
  },
  {
    key: 'action',
    label: 'Action',
    letter: 'A',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-400',
    prompt: 'What specific steps did you take? Focus on YOUR contributions, not the team\'s.',
    helpQuestions: [
      'What was the first step you took?',
      'What tools, methods, or strategies did you use?',
      'How did you collaborate with or influence others?',
      'What obstacles did you overcome along the way?',
    ],
  },
  {
    key: 'result',
    label: 'Result',
    letter: 'R',
    color: 'text-rose-700',
    bgColor: 'bg-rose-100',
    borderColor: 'border-rose-400',
    prompt: 'What was the outcome? Use numbers and metrics whenever possible.',
    helpQuestions: [
      'What measurable results did you achieve (%, $, time saved)?',
      'How did this impact the team, department, or company?',
      'What did you learn from this experience?',
      'Did you receive any recognition or feedback?',
    ],
  },
];

const QUESTION_TYPES: { value: QuestionType; label: string; icon: React.ReactNode; example: string }[] = [
  { value: 'leadership', label: 'Leadership', icon: <Target className="w-4 h-4" />, example: 'Tell me about a time you led a team through a difficult project.' },
  { value: 'teamwork', label: 'Teamwork', icon: <Users className="w-4 h-4" />, example: 'Describe a time you collaborated with others to achieve a goal.' },
  { value: 'problem-solving', label: 'Problem-Solving', icon: <Zap className="w-4 h-4" />, example: 'Tell me about a time you solved a complex problem.' },
  { value: 'conflict', label: 'Conflict', icon: <Shield className="w-4 h-4" />, example: 'Describe a time you had a disagreement with a coworker.' },
  { value: 'time-management', label: 'Time Management', icon: <Clock className="w-4 h-4" />, example: 'Tell me about a time you managed competing priorities.' },
  { value: 'achievement', label: 'Achievement', icon: <Trophy className="w-4 h-4" />, example: 'What is your greatest professional accomplishment?' },
  { value: 'failure', label: 'Failure / Learning', icon: <AlertTriangle className="w-4 h-4" />, example: 'Tell me about a time you failed and what you learned.' },
];

const TAILORED_PROMPTS: Record<QuestionType, Record<StepKey, string[]>> = {
  leadership: {
    situation: ['What leadership challenge did your team face?', 'Was the team new, underperforming, or going through change?'],
    task: ['What was your leadership mandate?', 'Were you formally or informally leading?'],
    action: ['How did you motivate or align the team?', 'Did you delegate, coach, or make tough calls?'],
    result: ['How did team performance change?', 'Did morale, velocity, or output improve measurably?'],
  },
  teamwork: {
    situation: ['What project required cross-functional collaboration?', 'Were there diverse or remote team members?'],
    task: ['What was your specific role within the team?', 'What did the team need to deliver?'],
    action: ['How did you contribute beyond your own scope?', 'How did you handle different working styles?'],
    result: ['What did the team accomplish together?', 'How was the collaboration recognized?'],
  },
  'problem-solving': {
    situation: ['What was the problem and who was affected?', 'How long had the problem existed?'],
    task: ['What were you expected to solve or improve?', 'Were there constraints (budget, time, tools)?'],
    action: ['What analytical approach did you use?', 'Did you test multiple solutions?'],
    result: ['How was the problem resolved?', 'What was the measurable impact of your solution?'],
  },
  conflict: {
    situation: ['Who was the conflict with and what caused it?', 'What was at stake if it wasn\'t resolved?'],
    task: ['What was your role in resolving the conflict?', 'Was resolution expected of you or did you step up?'],
    action: ['How did you approach the other person?', 'What communication techniques did you use?'],
    result: ['How was the conflict resolved?', 'Did the relationship or process improve afterward?'],
  },
  'time-management': {
    situation: ['What competing priorities did you face?', 'Was there a tight or shifting deadline?'],
    task: ['What was expected of you and by when?', 'Were multiple stakeholders waiting on your work?'],
    action: ['How did you prioritize tasks?', 'Did you use any frameworks, tools, or delegation?'],
    result: ['Did you meet all deadlines?', 'What was the quality and impact of the final deliverables?'],
  },
  achievement: {
    situation: ['What was the context of your biggest win?', 'What was the state of things before you got involved?'],
    task: ['What ambitious goal did you set or accept?', 'Why was this especially challenging?'],
    action: ['What exceptional effort or strategy did you employ?', 'What did you do differently from others?'],
    result: ['What was the standout result?', 'How did this achievement compare to benchmarks or expectations?'],
  },
  failure: {
    situation: ['What was the project or initiative that didn\'t go as planned?', 'What were the initial expectations?'],
    task: ['What were you responsible for?', 'What was at risk if things went wrong?'],
    action: ['What specifically went wrong and what was your part in it?', 'How did you respond when you realized it was failing?'],
    result: ['What was the outcome?', 'What concrete lesson did you take away, and how did you apply it later?'],
  },
};

// ---------------------------------------------------------------------------
// Pre-built example STAR answers
// ---------------------------------------------------------------------------

const EXAMPLE_ANSWERS: Record<QuestionType, STARInputs & { question: string }> = {
  leadership: {
    question: 'Tell me about a time you led a team through a difficult project.',
    situation: 'Our engineering team of 8 was struggling to deliver a critical platform migration on schedule. Two senior developers had recently left, morale was low, and the project was 3 weeks behind the original timeline. Stakeholders were losing confidence in the team\'s ability to deliver.',
    task: 'As the newly promoted tech lead, I was responsible for getting the migration back on track, rebuilding team confidence, and delivering the project within the revised 6-week deadline set by the VP of Engineering.',
    action: 'I held individual 1-on-1s with each team member to understand their blockers and concerns. I restructured the project into 2-week sprints with clear milestones, redistributed work based on each person\'s strengths, and created a daily 15-minute standup to surface issues early. I also negotiated with the product team to defer two non-critical features, and I personally took on the most complex integration work to unblock others.',
    result: 'We delivered the migration 4 days ahead of the revised deadline. System uptime improved from 97.2% to 99.8%, and page load times dropped by 40%. The team\'s sprint velocity increased by 35% over the next quarter, and our engagement survey scores went from 3.1 to 4.4 out of 5. I was nominated for the company\'s quarterly leadership award.',
  },
  teamwork: {
    question: 'Describe a time you collaborated with others to achieve a goal.',
    situation: 'Our company was launching a new mobile app feature that required coordination between the product, design, engineering, and marketing teams across three different time zones. Previous cross-functional projects had suffered from misaligned priorities and communication breakdowns.',
    task: 'As the product manager, I was responsible for ensuring all four teams were aligned on requirements, timeline, and launch criteria. I needed to deliver a polished feature within 8 weeks for a major industry conference.',
    action: 'I created a shared project Kanban board in Notion with clear ownership columns for each team. I established weekly cross-functional syncs and a dedicated Slack channel for real-time questions. When the design team was falling behind, I facilitated a workshop to fast-track decisions. I also built a simple launch readiness checklist that all team leads had to sign off on before each milestone.',
    result: 'We launched the feature 2 days before the conference. The feature drove a 28% increase in user engagement within the first month and was highlighted in the conference keynote. The process I created was adopted as the standard for all future cross-team projects, reducing average cross-functional project delivery time by 20%.',
  },
  'problem-solving': {
    question: 'Tell me about a time you solved a complex problem.',
    situation: 'Our e-commerce platform was experiencing a mysterious 15% drop in checkout completion rates over a 2-week period. The engineering and product teams had been investigating for 5 days with no clear answer. Revenue was declining by roughly $120K per week.',
    task: 'As the senior data analyst, I was asked to identify the root cause and recommend a fix within 48 hours, as leadership was considering rolling back recent deployments as a stopgap.',
    action: 'I built a funnel analysis comparing user behavior before and after the drop, segmented by device type, browser, and geography. I discovered the issue was isolated to mobile Safari users, which led me to examine a recent CSS update. I collaborated with a front-end engineer to replicate the bug: a payment button was being obscured by a floating element on certain screen sizes. I documented the findings with screenshots and data and presented the root cause to the team within 18 hours.',
    result: 'The engineering team deployed a fix within 6 hours of my report. Checkout completion rates recovered to previous levels within 2 days, saving an estimated $240K in potential lost revenue. My analysis framework was turned into an automated monitoring dashboard that now catches similar funnel drops within 4 hours instead of 5 days.',
  },
  conflict: {
    question: 'Describe a time you had a disagreement with a coworker.',
    situation: 'During a product redesign, the lead designer and I (the product manager) had fundamentally different visions for the new user onboarding flow. The designer wanted a visually rich, multi-screen guided tour, while I believed the data supported a simpler, task-based approach. The disagreement was creating tension and blocking progress for 2 weeks.',
    task: 'I needed to resolve the disagreement constructively so we could move forward with a decision that was best for the users and the business, while maintaining a healthy working relationship with the designer.',
    action: 'I scheduled a dedicated meeting where we each had 15 minutes to present our approach with supporting evidence. I came prepared with user research data and competitor benchmarks. I actively listened to the designer\'s rationale and acknowledged the strengths of their approach. I then proposed we run a 2-week A/B test with a small user segment to let the data decide. I offered to split the implementation work so neither approach required more effort.',
    result: 'The A/B test showed the task-based approach had a 23% higher completion rate, but users rated the guided tour higher on satisfaction. We ended up combining the best of both: a streamlined task flow with optional visual walkthroughs. The designer and I became close collaborators going forward, and our onboarding completion rate improved by 31% compared to the original design.',
  },
  'time-management': {
    question: 'Tell me about a time you managed competing priorities.',
    situation: 'During Q4, I was simultaneously managing three projects: a client website launch due in 2 weeks, an internal tool migration with a compliance deadline, and onboarding two new team members. My manager was on unexpected medical leave, so there was no one to delegate upward to.',
    task: 'I needed to ensure all three initiatives met their deadlines without sacrificing quality, while also making sure the new hires felt supported and productive during their first month.',
    action: 'I mapped out all deliverables on a single timeline and identified dependencies and bottlenecks. I delegated specific client revision rounds to a senior team member and created a structured onboarding plan so the new hires could self-serve for most tasks. I blocked "deep work" mornings for the compliance migration (the highest-stakes item) and scheduled all meetings and reviews in the afternoon. I also set up automated status updates to stakeholders so I could reduce time spent on reporting.',
    result: 'The client website launched on time and received positive feedback — the client renewed their contract for the following year. The compliance migration was completed 3 days early, avoiding a potential $50K penalty. Both new hires passed their 30-day reviews with high marks and were independently contributing within 3 weeks. My manager later adopted my prioritization framework as a team standard.',
  },
  achievement: {
    question: 'What is your greatest professional accomplishment?',
    situation: 'Our SaaS company was losing enterprise customers to a competitor that had launched an advanced analytics dashboard. Customer churn in the enterprise segment had increased by 18% over two quarters, and leadership identified this as the top strategic priority.',
    task: 'As the product lead, I was tasked with designing, building, and launching a competitive analytics feature within one quarter — half the typical timeline for a project of this scope — with a fixed team of 5 engineers and 2 designers.',
    action: 'I ran rapid customer discovery interviews with 12 churned and 8 at-risk accounts in the first week to identify the exact capabilities they needed. I used these insights to ruthlessly prioritize a lean feature set. I implemented a "build-measure-learn" cycle with biweekly beta releases to 5 pilot customers. I also partnered with the sales team to create a competitive comparison guide and win-back campaign running in parallel with development.',
    result: 'We launched the analytics dashboard in 11 weeks. Within 90 days of launch, enterprise churn dropped by 22%. The win-back campaign recovered 4 of the 12 churned accounts, representing $380K in annual recurring revenue. The feature became the most-used module in the platform, with 74% weekly active usage among enterprise customers. I was promoted to Senior Product Lead and the project was featured in the company\'s annual report.',
  },
  failure: {
    question: 'Tell me about a time you failed and what you learned.',
    situation: 'I was leading the rollout of a new CRM system across our 200-person sales organization. The legacy system was outdated, and leadership had approved a $150K budget for the transition. Everyone was excited about the improved capabilities.',
    task: 'I was responsible for selecting the platform, managing the data migration, and ensuring sales reps were trained and fully transitioned within 3 months.',
    action: 'I chose a platform based primarily on features and cost, without adequately piloting it with actual users. I scheduled a single 2-hour training session per team and planned a "big bang" cutover for the entire organization on one weekend. When the cutover happened, sales reps were confused by the new workflows, critical custom fields hadn\'t migrated properly, and the reporting dashboards didn\'t match what managers were accustomed to. Several reps reverted to spreadsheets, and pipeline visibility dropped significantly.',
    result: 'The full adoption took 5 months instead of 3, and we temporarily lost 3 weeks of accurate pipeline data. After the initial failure, I course-corrected by creating a phased rollout plan with team-specific training, appointing CRM champions in each department, and holding daily office hours for the first month. The CRM was eventually fully adopted with a 92% satisfaction score. The experience taught me to always pilot with real users before a full rollout, never underestimate change management, and build in buffer time for enterprise tool transitions. I\'ve applied these principles to every technology rollout since.',
  },
};

// ---------------------------------------------------------------------------
// Strong action verbs for scoring & suggestions
// ---------------------------------------------------------------------------

const STRONG_ACTION_VERBS = [
  'led', 'managed', 'developed', 'created', 'designed', 'built', 'launched',
  'implemented', 'delivered', 'achieved', 'improved', 'increased', 'reduced',
  'optimized', 'streamlined', 'spearheaded', 'orchestrated', 'negotiated',
  'mentored', 'coached', 'transformed', 'pioneered', 'automated', 'initiated',
  'established', 'executed', 'generated', 'resolved', 'collaborated',
  'facilitated', 'analyzed', 'drove', 'accelerated', 'revamped', 'overhauled',
  'secured', 'influenced', 'championed', 'consolidated', 'architected',
  'restructured', 'scaled', 'prioritized', 'delegated', 'navigated',
];

const METRIC_PATTERNS = [
  /\d+%/, /\$\d+/, /\d+x/, /\d+\s*(k|K|million|billion)/,
  /\d+\s*(hours?|days?|weeks?|months?|years?)/, /\d+\s*(people|team members|employees|users|customers|clients|accounts)/,
  /\d+\s*(projects?|initiatives?|features?|products?)/, /\d+\s*(times?|fold)/,
  /\d+(\.\d+)?%/, /\d{1,3}(,\d{3})+/,
];

const TRANSITION_PHRASES = {
  situationToTask: [
    'Given this context,',
    'In light of this situation,',
    'Recognizing the urgency,',
    'Faced with this challenge,',
    'With this backdrop,',
  ],
  taskToAction: [
    'To address this,',
    'I took a strategic approach:',
    'I immediately got to work.',
    'I developed a clear plan of action.',
    'Here is how I tackled it.',
  ],
  actionToResult: [
    'As a result of these efforts,',
    'The impact was significant.',
    'These actions led to measurable outcomes.',
    'The results spoke for themselves.',
    'This approach delivered strong results.',
  ],
};

// ---------------------------------------------------------------------------
// Scoring algorithm
// ---------------------------------------------------------------------------

function scoreAnswer(inputs: STARInputs): { total: number; breakdown: { label: string; score: number; tip: string }[] } {
  const breakdown: { label: string; score: number; tip: string }[] = [];

  // 1. Specificity: average word count across sections (target 40-100 words each)
  const wordCounts = Object.values(inputs).map((v) => v.trim().split(/\s+/).filter(Boolean).length);
  const avgWords = wordCounts.reduce((a, b) => a + b, 0) / 4;
  let specificityScore: number;
  if (avgWords < 15) specificityScore = 20;
  else if (avgWords < 30) specificityScore = 50;
  else if (avgWords < 50) specificityScore = 75;
  else if (avgWords <= 120) specificityScore = 95;
  else specificityScore = 80; // slightly verbose
  breakdown.push({
    label: 'Specificity & Detail',
    score: specificityScore,
    tip: avgWords < 30
      ? 'Add more specific details to each section. Aim for 40-80 words per section.'
      : avgWords > 120
        ? 'Consider trimming for conciseness. Aim for 40-80 words per section.'
        : 'Good level of detail in your answer.',
  });

  // 2. Action verbs
  const actionText = inputs.action.toLowerCase();
  const verbsFound = STRONG_ACTION_VERBS.filter((v) => actionText.includes(v));
  const verbScore = Math.min(100, verbsFound.length * 20);
  breakdown.push({
    label: 'Action Verbs',
    score: Math.max(verbScore, 10),
    tip: verbsFound.length < 3
      ? `Use stronger action verbs like: ${STRONG_ACTION_VERBS.slice(0, 6).join(', ')}. Found ${verbsFound.length} strong verb(s).`
      : `Great use of action verbs: ${verbsFound.slice(0, 5).join(', ')}.`,
  });

  // 3. Metrics & quantification in result
  const resultText = inputs.result;
  const metricsFound = METRIC_PATTERNS.filter((p) => p.test(resultText));
  const metricScore = Math.min(100, metricsFound.length * 30);
  breakdown.push({
    label: 'Metrics & Impact',
    score: Math.max(metricScore, 10),
    tip: metricsFound.length < 2
      ? 'Add quantifiable metrics: percentages, dollar amounts, time saved, number of people impacted.'
      : 'Excellent use of metrics to demonstrate impact.',
  });

  // 4. First-person focus (should be about "I", not only "we")
  const allText = Object.values(inputs).join(' ');
  const iCount = (allText.match(/\bI\b/g) || []).length;
  const weCount = (allText.match(/\b[Ww]e\b/g) || []).length;
  const firstPersonScore = iCount >= 3 ? (iCount > weCount ? 90 : 65) : 30;
  breakdown.push({
    label: 'Personal Ownership',
    score: firstPersonScore,
    tip: iCount < 3
      ? 'Use more "I" statements to highlight YOUR specific contributions, not just the team\'s.'
      : iCount <= weCount
        ? 'Good, but try to emphasize your individual contributions more. Use "I" more than "we".'
        : 'Strong personal ownership in your narrative.',
  });

  // 5. Balance across sections
  const maxWords = Math.max(...wordCounts);
  const minWords = Math.min(...wordCounts);
  const balanceRatio = maxWords > 0 ? minWords / maxWords : 0;
  const balanceScore = Math.round(balanceRatio * 100);
  breakdown.push({
    label: 'Section Balance',
    score: Math.max(balanceScore, 10),
    tip: balanceRatio < 0.3
      ? 'Some sections are much shorter than others. Try to balance the detail across all four STAR parts.'
      : 'Sections are well-balanced in length and detail.',
  });

  const total = Math.round(breakdown.reduce((sum, b) => sum + b.score, 0) / breakdown.length);

  return { total, breakdown };
}

// ---------------------------------------------------------------------------
// Polishing algorithm
// ---------------------------------------------------------------------------

function buildPolishedAnswer(inputs: STARInputs): BuiltAnswer {
  const { total, breakdown } = scoreAnswer(inputs);

  // Pick random transition phrases
  const t1 = TRANSITION_PHRASES.situationToTask[Math.floor(Math.random() * TRANSITION_PHRASES.situationToTask.length)];
  const t2 = TRANSITION_PHRASES.taskToAction[Math.floor(Math.random() * TRANSITION_PHRASES.taskToAction.length)];
  const t3 = TRANSITION_PHRASES.actionToResult[Math.floor(Math.random() * TRANSITION_PHRASES.actionToResult.length)];

  // Clean up each section
  const cleanSituation = inputs.situation.trim().replace(/\.\s*$/, '') + '.';
  const cleanTask = inputs.task.trim().replace(/\.\s*$/, '') + '.';
  const cleanAction = inputs.action.trim().replace(/\.\s*$/, '') + '.';
  const cleanResult = inputs.result.trim().replace(/\.\s*$/, '') + '.';

  const polished = `${cleanSituation}\n\n${t1} ${cleanTask.charAt(0).toLowerCase() + cleanTask.slice(1)}\n\n${t2} ${cleanAction}\n\n${t3} ${cleanResult}`;

  const sections: { key: StepKey; label: string; text: string }[] = [
    { key: 'situation', label: 'Situation', text: cleanSituation },
    { key: 'task', label: 'Task', text: cleanTask },
    { key: 'action', label: 'Action', text: cleanAction },
    { key: 'result', label: 'Result', text: cleanResult },
  ];

  return {
    polished,
    sections,
    score: total,
    feedback: breakdown,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function STARBuilderContent() {
  const [currentStep, setCurrentStep] = useState(0);
  const [questionType, setQuestionType] = useState<QuestionType | ''>('');
  const [inputs, setInputs] = useState<STARInputs>({
    situation: '',
    task: '',
    action: '',
    result: '',
  });
  const [builtAnswer, setBuiltAnswer] = useState<BuiltAnswer | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [activeExample, setActiveExample] = useState<QuestionType>('leadership');

  const step = STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  const allFilled = Object.values(inputs).every((v) => v.trim().length > 0);

  // Determine prompt helper questions to show based on optional question type
  const helperQuestions = useMemo(() => {
    if (questionType && TAILORED_PROMPTS[questionType]) {
      return TAILORED_PROMPTS[questionType][step.key];
    }
    return step.helpQuestions;
  }, [questionType, step, currentStep]);

  const handleInputChange = (value: string) => {
    setInputs((prev) => ({ ...prev, [step.key]: value }));
  };

  const handleNext = () => {
    if (!isLastStep) setCurrentStep((s) => s + 1);
  };

  const handlePrev = () => {
    if (!isFirstStep) setCurrentStep((s) => s - 1);
  };

  const handleBuild = () => {
    const result = buildPolishedAnswer(inputs);
    setBuiltAnswer(result);
    setTimeout(() => {
      document.getElementById('star-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setInputs({ situation: '', task: '', action: '', result: '' });
    setBuiltAnswer(null);
    setCurrentStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoadExample = (type: QuestionType) => {
    const ex = EXAMPLE_ANSWERS[type];
    setInputs({
      situation: ex.situation,
      task: ex.task,
      action: ex.action,
      result: ex.result,
    });
    setQuestionType(type);
    setShowExamples(false);
    setCurrentStep(0);
  };

  const sectionColors: Record<StepKey, { badge: string; border: string }> = {
    situation: { badge: 'bg-blue-100 text-blue-700', border: 'border-l-blue-500' },
    task: { badge: 'bg-emerald-100 text-emerald-700', border: 'border-l-emerald-500' },
    action: { badge: 'bg-amber-100 text-amber-700', border: 'border-l-amber-500' },
    result: { badge: 'bg-rose-100 text-rose-700', border: 'border-l-rose-500' },
  };

  return (
    <>
      <ToolHero
        title="STAR Method Builder"
        description="Build polished behavioral interview answers step by step. Situation, Task, Action, Result — structured for maximum impact."
        category="Interview Prep"
      />

      <main className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Question Type Selector */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">
                What type of behavioral question are you preparing for?
              </h2>
              <span className="text-sm text-gray-400 font-normal">(optional)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {QUESTION_TYPES.map((qt) => (
                <button
                  key={qt.value}
                  onClick={() => setQuestionType(questionType === qt.value ? '' : qt.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    questionType === qt.value
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {qt.icon}
                  {qt.label}
                </button>
              ))}
            </div>
            {questionType && (
              <p className="mt-3 text-sm text-gray-500 italic">
                Example question: &ldquo;{QUESTION_TYPES.find((q) => q.value === questionType)?.example}&rdquo;
              </p>
            )}
          </div>

          {/* Step Wizard */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8">
            {/* Progress Indicator */}
            <div className="flex items-center justify-between mb-8">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center flex-1 last:flex-none">
                  <button
                    onClick={() => setCurrentStep(i)}
                    className={`flex items-center gap-2 group transition-all ${
                      i === currentStep ? 'scale-105' : ''
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                        i < currentStep
                          ? 'bg-green-500 text-white'
                          : i === currentStep
                            ? 'bg-blue-600 text-white shadow-lg ring-4 ring-blue-200'
                            : inputs[s.key].trim()
                              ? 'bg-green-100 text-green-700 border-2 border-green-300'
                              : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                      }`}
                    >
                      {i < currentStep || (i !== currentStep && inputs[s.key].trim()) ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        s.letter
                      )}
                    </div>
                    <span
                      className={`text-sm font-semibold hidden sm:inline ${
                        i === currentStep ? 'text-blue-700' : 'text-gray-400'
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded-full transition-all ${
                        i < currentStep ? 'bg-green-400' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Step Content */}
            <div className={`border-l-4 ${step.borderColor} pl-6 mb-6`}>
              <div className="flex items-center gap-3 mb-2">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${step.bgColor} ${step.color}`}>
                  {step.letter}
                </span>
                <h3 className="text-xl font-bold text-gray-900">{step.label}</h3>
                <span className="text-sm text-gray-400">Step {currentStep + 1} of 4</span>
              </div>
              <p className="text-gray-600 mb-4">{step.prompt}</p>

              {/* Helper prompts */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-semibold text-gray-700">
                    {questionType ? 'Tailored prompts' : 'Questions to guide you'}
                  </span>
                </div>
                <ul className="space-y-1">
                  {helperQuestions.map((q, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>

              <textarea
                value={inputs[step.key]}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={step.prompt}
                rows={6}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none text-gray-800 leading-relaxed"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">
                  {inputs[step.key].trim().split(/\s+/).filter(Boolean).length} words
                </span>
                {inputs[step.key].trim().length > 0 && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Section filled
                  </span>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrev}
                disabled={isFirstStep}
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isFirstStep
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex items-center gap-3">
                {isLastStep && allFilled ? (
                  <button
                    onClick={handleBuild}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-base"
                  >
                    <Sparkles className="w-5 h-5" />
                    Build My Answer
                  </button>
                ) : isLastStep && !allFilled ? (
                  <span className="text-sm text-gray-400">Fill all 4 sections to build your answer</span>
                ) : (
                  <button
                    onClick={handleNext}
                    className="inline-flex items-center gap-1.5 bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-xl shadow hover:bg-blue-700 transition-all text-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick Status: which sections are filled */}
          {!builtAnswer && (
            <div className="flex items-center justify-center gap-3 mb-8">
              {STEPS.map((s) => (
                <div
                  key={s.key}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    inputs[s.key].trim()
                      ? `${s.bgColor} ${s.color}`
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {s.letter} - {s.label}
                  {inputs[s.key].trim() && <CheckCircle2 className="w-3.5 h-3.5" />}
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          {builtAnswer && (
            <div id="star-results">
              {/* Score */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <ScoreGauge score={builtAnswer.score} label="Strength Score" />
                  <div className="flex-1 w-full">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Score Breakdown</h3>
                    <div className="space-y-3">
                      {builtAnswer.feedback.map((fb) => (
                        <div key={fb.label}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{fb.label}</span>
                            <span className="text-sm font-bold text-gray-900">{fb.score}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ease-out ${
                                fb.score < 40 ? 'bg-red-500' : fb.score < 70 ? 'bg-amber-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${fb.score}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500">{fb.tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Polished Answer */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Your STAR Answer</h2>
                  <CopyButton
                    text={builtAnswer.polished}
                    label="Copy Full Answer"
                    className="!px-4 !py-2 !text-base"
                  />
                </div>

                {/* Combined polished view */}
                <div className="bg-gray-50 rounded-xl p-6 mb-8 text-gray-800 leading-relaxed whitespace-pre-line">
                  {builtAnswer.polished}
                </div>

                {/* Section-by-section breakdown */}
                <h3 className="text-lg font-bold text-gray-900 mb-4">Section Breakdown</h3>
                <div className="space-y-4">
                  {builtAnswer.sections.map((sec) => {
                    const colors = sectionColors[sec.key];
                    const stepMeta = STEPS.find((s) => s.key === sec.key)!;
                    return (
                      <div
                        key={sec.key}
                        className={`border-l-4 ${colors.border} bg-white rounded-r-xl p-5 shadow-sm`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md font-bold text-xs ${colors.badge}`}>
                            {stepMeta.letter}
                          </span>
                          <span className="font-bold text-gray-900">{sec.label}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{sec.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-bold text-gray-900">Tips for a Strong STAR Answer</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h4 className="font-bold text-blue-800 mb-1 text-sm">Situation</h4>
                    <p className="text-sm text-blue-700">Keep it concise. Set the scene in 2-3 sentences. Include the company, team, and context — but don&apos;t over-explain.</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <h4 className="font-bold text-emerald-800 mb-1 text-sm">Task</h4>
                    <p className="text-sm text-emerald-700">Clarify YOUR role, not the team&apos;s. What were you specifically responsible for? Why was it important?</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <h4 className="font-bold text-amber-800 mb-1 text-sm">Action</h4>
                    <p className="text-sm text-amber-700">This should be the longest section. Use strong action verbs (led, built, analyzed) and explain your decision-making process.</p>
                  </div>
                  <div className="bg-rose-50 rounded-xl p-4">
                    <h4 className="font-bold text-rose-800 mb-1 text-sm">Result</h4>
                    <p className="text-sm text-rose-700">Always quantify. Use percentages, dollar amounts, time saved, or people impacted. End with what you learned if relevant.</p>
                  </div>
                </div>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.indeed.com/career-advice/interviewing/how-to-use-the-star-interview-response-technique" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Use the STAR Interview Method</a>
                  {' · '}
                  <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
                  {' · '}
                  <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">SHRM — Behavioral Interview Resources</a>
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                >
                  Start Over
                </button>
                <button
                  onClick={handleBuild}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  Rebuild Answer
                </button>
              </div>

              {/* CTA */}
              <ToolCTA
                headline="Ace your behavioral interviews"
                description="A strong resume gets you the interview. Superpower Resume helps you build one that highlights your best STAR stories."
                buttonText="Build My Resume Free"
              />
            </div>
          )}

          {/* Examples Section */}
          <div className="mt-12">
            <button
              onClick={() => setShowExamples(!showExamples)}
              className="flex items-center gap-2 mx-auto mb-6 text-blue-600 hover:text-blue-700 font-semibold transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              {showExamples ? 'Hide' : 'View'} Pre-Built STAR Examples
              <ChevronDown className={`w-4 h-4 transition-transform ${showExamples ? 'rotate-180' : ''}`} />
            </button>

            {showExamples && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">STAR Method Examples</h2>
                <p className="text-gray-500 mb-6">
                  Complete examples for 7 common behavioral interview question types. Click &ldquo;Load this example&rdquo; to use it as a starting point.
                </p>

                {/* Example type tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {QUESTION_TYPES.map((qt) => (
                    <button
                      key={qt.value}
                      onClick={() => setActiveExample(qt.value)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeExample === qt.value
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {qt.icon}
                      {qt.label}
                    </button>
                  ))}
                </div>

                {/* Active Example */}
                {(() => {
                  const ex = EXAMPLE_ANSWERS[activeExample];
                  const qtMeta = QUESTION_TYPES.find((q) => q.value === activeExample)!;
                  return (
                    <div>
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-2 mb-1">
                          {qtMeta.icon}
                          <span className="text-sm font-bold text-gray-700">{qtMeta.label}</span>
                        </div>
                        <p className="text-gray-800 font-medium italic">&ldquo;{ex.question}&rdquo;</p>
                      </div>

                      <div className="space-y-3 mb-6">
                        {STEPS.map((s) => {
                          const colors = sectionColors[s.key];
                          return (
                            <div
                              key={s.key}
                              className={`border-l-4 ${colors.border} bg-white rounded-r-xl p-4`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md font-bold text-xs ${colors.badge}`}>
                                  {s.letter}
                                </span>
                                <span className="font-bold text-gray-900 text-sm">{s.label}</span>
                              </div>
                              <p className="text-sm text-gray-700 leading-relaxed">{ex[s.key]}</p>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex items-center justify-between">
                        <CopyButton
                          text={`${ex.situation}\n\n${ex.task}\n\n${ex.action}\n\n${ex.result}`}
                          label="Copy Example"
                        />
                        <button
                          onClick={() => handleLoadExample(activeExample)}
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold py-2.5 px-6 rounded-xl shadow hover:from-blue-700 hover:to-blue-600 transition-all text-sm"
                        >
                          <BarChart3 className="w-4 h-4" />
                          Load This Example
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Bottom CTA if no result yet */}
          {!builtAnswer && (
            <div className="mt-12">
              <ToolCTA
                headline="Ace your behavioral interviews"
                description="A strong resume gets you the interview. Superpower Resume helps you build one that highlights your best STAR stories."
                buttonText="Build My Resume Free"
              />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
